<script setup>
/**
 * The Journal: a book the seeker keeps (journal redesign 2026-10-27,
 * `Claude outputs/journal-redesign-2026-10-27/DESIGN.md`; contract API.md).
 *
 * A plum leather book with a ribbon; each day a dated paper page holding only
 * what happened, in diary order. On a phone it is one page (the contents
 * open from ☰ Contents); from 900px wide it is an open book, the contents on
 * the left page and the day on the right. Pages turn on the spine (or cross-
 * fade with reduced motion). The cover opens once; with the opt-in clasp it
 * closes again on leaving the Journal or after five minutes away.
 */
import { noteRoomOrigin } from '@/utils/mystic-room'
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { store } from '@/store'
import {
  normalizeBook, mergeDays, pageKeys, neighbours, openingDay, dayOf, hiddenMap, shortDate, withMoment, withoutMoment,
  withEntry, withoutDay, plainEntry, withDay, normalizeThreadTag,
} from '@/utils/journal-book'
import {
  normalizeThread, normalizeChapter, normalizeThreadList, withOwn, withThreadName, withDayThreads, otherThreads, withChapterState,
} from '@/utils/journal-practice'
import { clasp, openClasp, shutClasp, watchAway } from '@/composables/useJournalClasp'
import { passkeyAvailable, bioName, createPasskey, getPasskey } from '@/utils/webauthn'
import {
  getJournalStatus, getJournalDays, journalItem, journalPage, journalSeen, journalOnce, journalSetPin, journalUnlock,
  journalLockRefresh, journalLockOff, journalPasskeyRegisterOptions, journalPasskeyRegister, journalPasskeyOptions, journalPasskeyVerify,
  journalForgot, journalForgotVerify, careRevoke,
  journalLine, getJournalThreads, getJournalThread, journalThreadRename, journalThreadTag, journalThreadUntag, getJournalCalendar,
  getJournalChapter, journalChapterSave, getJournalKept, journalUnkeep,
} from '@/api-service'
import { normalizeKept, normalizeOurStars, thumbsOf } from '@/utils/journal-kept'
import JournalPage from './JournalPage.vue'
import JournalContents from './JournalContents.vue'
import JournalCover from './JournalCover.vue'
import JournalClaspSlip from './JournalClaspSlip.vue'
import JournalEditor from './JournalEditor.vue'
import JournalThread from './JournalThread.vue'
import JournalChapter from './JournalChapter.vue'
import JournalKept from './JournalKept.vue'
import JournalStars from './JournalStars.vue'

const props = defineProps({
  initialDay: { type: String, default: '' },
  // Opened from the gift room's "I've kept these for you.": open on the gift's page.
  openReceived: { type: Boolean, default: false },
})
const emit = defineEmits(['dayChange', 'close', 'seen'])
const router = useRouter()

// Pages already seen this visit: their ink is drawn, their gift tucked in.
const played = reactive({})

const rootEl = ref(null)
const loading = ref(true)
const book = ref(normalizeBook({ days: [] }))
const cur = ref('')
const leaf = ref(null)            // { key, dir } while a leaf is turning
const leafEl = ref(null)
const shadeEl = ref(null)
const pageRef = ref(null)
const leafPageRef = ref(null)
const menu = ref('')
const calOpen = ref(false)
const searchOpen = ref(false)
const query = ref('')
const editKey = ref('')
const toast = ref('')
const sheet = ref(null)
const revoking = ref('')
const showHiddenPages = ref(false)
const desktop = ref(false)
const reduced = ref(false)
const writingKey = ref('')

// The cover: 'first' opens once; 'lock' is the clasp.
const cover = ref(null)
const coverRef = ref(null)
const coverShut = ref(false)      // 1440: the closed book sits to the right until it opens
const lockStep = ref('cover')
const lockEntry = ref('')
const lockErr = ref('')
const lockBusy = ref(false)
const shake = ref(0)
const resetToken = ref('')
const sentTo = ref('')
const devicePasskey = ref(false)
const bio = computed(() => (devicePasskey.value ? bioName() : ''))

// The clasp offer (once) and its PIN, chosen twice.
const slipStep = ref('')
const slipFromMenu = ref(false)
const setupPin = ref('')
const setupFirst = ref('')
const setupErr = ref('')
const setupBusy = ref(false)

const hidden = computed(() => hiddenMap(book.value, { showing: showHiddenPages.value }))
const keys = computed(() => pageKeys(book.value, { hiddenDays: hidden.value }))
const curDay = computed(() => dayOf(book.value, cur.value))
const leafDay = computed(() => (leaf.value ? dayOf(book.value, leaf.value.key) : null))
const nb = computed(() => neighbours(keys.value, cur.value))
const newest = computed(() => keys.value[keys.value.length - 1] || '')
const hiddenCount = computed(() => book.value.days.filter((d) => d.hidden).length)
const showSlip = computed(() => !!slipStep.value && (slipStep.value !== 'offer' || slipFromMenu.value || !!(curDay.value && curDay.value.entry)))
const editing = computed(() => !!editKey.value && editKey.value === cur.value)
const editText = computed(() => (curDay.value && curDay.value.entry ? curDay.value.entry.text : ''))
const locked = computed(() => cover.value === 'lock' && book.value.locked)
const status = ref({})

// The practice (journal practice 2026-10-28): a thread's page or a month's
// chapter, turned to in place of the day; the one-time "Everything on this
// page is yours" line.
const view = ref(null)            // { kind: 'thread', id } | { kind: 'chapter', month } | { kind: 'kept' } | { kind: 'stars', id }
const thread = ref(null)
const chapter = ref(null)
const viewLoading = ref(false)
const HINT_KEY = 'journal_edit_hint_seen'
const hint = ref(false)
try { hint.value = !window.localStorage.getItem(HINT_KEY) } catch (e) { hint.value = false }
const dayOthers = computed(() => otherThreads(book.value, curDay.value ? curDay.value.threads : []))
// The thread open on the right (DESIGN §8): its row on the contents is lit and
// its days ringed in the calendar, until the page turns back to a day.
const litThread = ref(null)
watch(view, (v) => { if (!v || v.kind !== 'thread') litThread.value = null })
const backLabel = computed(() => (cur.value ? shortDate(cur.value) : ''))

// Kept, and you & them (mystic page 2026-10-29): the Kept shelf and the kept
// Our Stars reports, on the contents; each opens on the right page.
const kept = ref(normalizeKept(null))
const stars = ref([])
const keptLoading = ref(false)
const openView = computed(() => (!view.value ? '' : view.value.kind === 'kept' ? 'kept' : view.value.kind === 'stars' ? `stars:${view.value.id}` : ''))
const openStarsItem = computed(() => (view.value && view.value.kind === 'stars' ? stars.value.find((o) => o.id === view.value.id) || null : null))

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))
function say (t, ms = 2600) { toast.value = t; clearTimeout(say.t); say.t = setTimeout(() => { toast.value = '' }, ms) }

// ── loading (API.md v1.1: journal/status/, then journal/days/ page by page) ──
const isLocked = (r) => !!r && r.ok === false && r.reason === 'locked'
const lockedBook = (lock) => normalizeBook({ locked: true, name: store.querent_first_name || store.querent_name || '', days: [] }, { lock, cover_opened: true })

/** The newest page of days, then older pages until `want` is in (a ?day= deep link). */
async function fetchBook (want = '') {
  const raw = await getJournalDays(store.session_id)
  if (isLocked(raw)) return lockedBook(raw.lock || (status.value && status.value.lock) || { on: true, pin: true })
  if (!raw || raw.ok === false) throw new Error((raw && raw.reason) || 'no_book')
  let b = normalizeBook(raw, status.value)
  for (let i = 0; i < 24 && want && b.nextBefore && want < (b.days[0] ? b.days[0].key : '9999'); i++) b = mergeDays(b, await getJournalDays(store.session_id, { before: b.nextBefore }))
  return b
}

/** The rest of the book, quietly, so the calendar, search and corners reach every page. */
let olderRun = 0
async function loadOlder () {
  const run = ++olderRun
  for (let i = 0; i < 24 && book.value.nextBefore && run === olderRun && !book.value.locked; i++) {
    const raw = await getJournalDays(store.session_id, { before: book.value.nextBefore }).catch(() => null)
    if (!raw || raw.ok === false || run !== olderRun) return
    book.value = mergeDays(book.value, raw)
  }
}

async function load ({ keepDay = false } = {}) {
  if (!store.session_id) { loading.value = false; return }
  try {
    status.value = (await getJournalStatus(store.session_id)) || {}
    const b = keepPractice(await fetchBook(keepDay ? cur.value : props.initialDay))
    book.value = b
    if (b.locked) {
      shutClasp()
      cover.value = 'lock'
      coverShut.value = true
      lockStep.value = 'cover'
      return
    }
    if (!keepDay || !keys.value.includes(cur.value)) cur.value = startDay(b)
    loadThreads()
    loadKept()
    if (!b.clasp.on && !b.clasp.offered && keys.value.length && !slipStep.value) slipStep.value = 'offer'
    if (!cover.value && keys.value.length && !b.coverOpened) {
      cover.value = 'first'
      coverShut.value = true
      if (visible()) openFirstCover(); else coverWaiting = true
    }
    loadOlder()
  } catch (e) {
    say('The journal could not open just now.')
  } finally { loading.value = false }
}

function startDay (b) {
  if (props.openReceived) {
    const g = [...b.days].reverse().find((d) => !d.hidden && d.moments.some((m) => m.kind === 'gift_in'))
    if (g) return g.key
  }
  return openingDay(b, { initialDay: props.initialDay, hiddenDays: hiddenMap(b) })
}

/** A 🔒 call answered "locked" (the token went stale): shut the book. */
function staleLock (r) { if (isLocked(r)) { closeClasp({ animate: false }); return true } return false }

// ── a page on show: its ink draws once, a new entry writes itself in once ─────
let playTimer = null
function onShown () {
  clearTimeout(playTimer)
  const k = cur.value
  // Behind another Altar tab nothing is read: no write-in, no seen, no ink.
  if (!k || cover.value || !visible()) return
  const day = curDay.value
  writingKey.value = (!reduced.value && day && day.entry && day.entry.unseen && !played[k]) ? k : ''
  if (day && day.entry && day.entry.unseen) markSeen(k)
  playTimer = setTimeout(() => { played[k] = true }, reduced.value ? 0 : 3200)
}
const seenSent = new Set()
async function markSeen (k) {
  if (seenSent.has(k)) return
  seenSent.add(k)
  try { await journalSeen(store.session_id, k) } catch (e) { /* the dot stays; no harm */ }
  // Keep `unseen` for the writing already under way; the next visit shows it written.
  book.value = { ...book.value, unseen: null }
  emit('seen', k)
}
watch(cur, (k) => { if (k) emit('dayChange', k); nextTick(onShown) })
// The host learns ?day= after the book has mounted (a cold load, a return from a
// reading): follow it to that page. Our own dayChange echoes back as a no-op.
watch(() => props.initialDay, (k) => {
  if (!k || k === cur.value || leaf.value) return
  if (keys.value.includes(k)) { cur.value = k; return }
  if (!book.value.locked && book.value.nextBefore && k < (keys.value[0] || '')) load({ keepDay: false })
})

// ── turning pages ──────────────────────────────────────────────────────────
const EASE = 'cubic-bezier(.45,.05,.3,1)'
async function turnTo (key) {
  // From a thread or a chapter: back to a day (the same one too), no leaf turning.
  const fromView = !!view.value
  if (fromView) { view.value = null; menu.value = ''; calOpen.value = false }
  if (!key || (key === cur.value && !fromView) || leaf.value) return
  menu.value = ''; calOpen.value = false; editKey.value = ''
  if (!desktop.value) { searchOpen.value = false }
  const page = pageRef.value && pageRef.value.$el
  if (fromView || reduced.value || !page || typeof page.animate !== 'function') {
    cur.value = key
    await nextTick()
    const el = pageRef.value && pageRef.value.$el
    if (el && typeof el.animate === 'function') el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160 })
    scrollTop(0)
    return
  }
  const dir = key > cur.value ? 1 : -1
  if (dir > 0) {
    const top = pageRef.value.scrollRef ? pageRef.value.scrollRef.scrollTop : 0
    leaf.value = { key: cur.value, dir }
    cur.value = key
    await nextTick()
    if (leafPageRef.value && leafPageRef.value.scrollRef) leafPageRef.value.scrollRef.scrollTop = top
    scrollTop(0)
    const a = leafEl.value.animate([{ transform: 'rotateY(0deg)' }, { transform: 'rotateY(-180deg)' }], { duration: 720, easing: EASE })
    shadeEl.value && shadeEl.value.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 360, fill: 'forwards' })
    await a.finished.catch(() => {})
    leaf.value = null
  } else {
    played[key] = true
    leaf.value = { key, dir }
    await nextTick()
    const a = leafEl.value.animate([{ transform: 'rotateY(-180deg)' }, { transform: 'rotateY(0deg)' }], { duration: 720, easing: EASE })
    shadeEl.value && shadeEl.value.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 720, fill: 'forwards' })
    await a.finished.catch(() => {})
    cur.value = key
    leaf.value = null
    await nextTick()
    scrollTop(0)
  }
}
function scrollTop (y) { const s = pageRef.value && pageRef.value.scrollRef; if (s) s.scrollTop = y }

function turnBy (n) { const i = keys.value.indexOf(cur.value); const k = keys.value[i + n]; if (k) turnTo(k) }
// In view: mounted and not under a v-show'n tab (the Altar keeps every tab mounted).
function visible () { return !!rootEl.value && rootEl.value.isConnected && !rootEl.value.closest('[style*="display: none"]') }
function onKey (e) {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
  if (!visible() || cover.value || editKey.value || sheet.value || view.value) return
  if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement && document.activeElement.tagName) || '') || (document.activeElement && document.activeElement.isContentEditable)) return
  turnBy(e.key === 'ArrowRight' ? 1 : -1)
}
let x0 = null; let y0 = 0
function onPointerDown (e) { if (e.pointerType === 'mouse' || view.value || /INPUT|TEXTAREA/.test((e.target && e.target.tagName) || '')) return; x0 = e.clientX; y0 = e.clientY }
function onPointerUp (e) {
  if (x0 === null) return
  const dx = e.clientX - x0; const dy = e.clientY - y0; x0 = null
  if (Math.abs(dx) > 60 && Math.abs(dy) < 50 && !cover.value && !editKey.value) turnBy(dx < 0 ? 1 : -1)
}
function onDocClick () { if (menu.value || calOpen.value) { menu.value = ''; calOpen.value = false } }

// ── the cover ──────────────────────────────────────────────────────────────
const COVER_EASE = 'cubic-bezier(.45,.05,.25,1)'
let coverWaiting = false
// The Journal came into view (its tab opened, or the page loaded on it).
function onBecameVisible () { if (!visible()) return; if (coverWaiting) openFirstCover(); else onShown() }
async function openFirstCover () {
  coverWaiting = false
  // The Quest's First Star hero plays first; the two never play together.
  for (let i = 0; i < 120 && store.first_star_hero_open; i++) await sleep(250)
  await sleep(reduced.value ? 500 : 900)
  await swingOpen()
  book.value = { ...book.value, coverOpened: true }
  try { await journalOnce(store.session_id, 'cover') } catch (e) { /* it opens again next time; no harm */ }
}

async function swingOpen () {
  await nextTick()
  const el = coverRef.value && coverRef.value.coverEl
  const finish = () => { cover.value = null; coverShut.value = false; nextTick(onShown) }
  if (!el || typeof el.animate !== 'function') return finish()
  if (reduced.value) {
    coverShut.value = false
    await el.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 300, fill: 'forwards' }).finished.catch(() => {})
    return finish()
  }
  const strap = coverRef.value.strapEl; const knob = coverRef.value.claspEl
  if (strap && knob) {
    knob.animate([{ transform: 'none' }, { transform: 'rotate(-28deg) translateY(-4px)' }], { duration: 260, easing: 'ease-out', fill: 'forwards' })
    strap.animate([{ transform: 'none', opacity: 1 }, { transform: 'translateX(90px)', opacity: 0 }], { duration: 420, delay: 240, easing: 'cubic-bezier(.5,0,.7,.4)', fill: 'forwards' })
    await sleep(620)
  }
  coverShut.value = false
  await el.animate([{ transform: 'rotateY(0deg)', filter: 'brightness(1)' }, { transform: 'rotateY(-92deg)', filter: 'brightness(.7)', offset: 0.5 }, { transform: 'rotateY(-180deg)', filter: 'brightness(.5)' }],
    { duration: 1100, easing: COVER_EASE, fill: 'forwards' }).finished.catch(() => {})
  finish()
}

/** The clasp closes: the cover swings shut, the strap snaps over, the pages go. */
async function closeClasp ({ animate = true } = {}) {
  menu.value = ''; calOpen.value = false; sheet.value = null; editKey.value = ''; slipStep.value = ''
  shutClasp()
  const forget = () => { book.value = { ...book.value, locked: true, days: [] }; lockStep.value = 'cover'; lockEntry.value = ''; lockErr.value = '' }
  const show = animate && !reduced.value && visible()
  cover.value = 'lock'
  if (!show) { coverShut.value = true; forget(); return }
  await nextTick()
  const el = coverRef.value && coverRef.value.coverEl
  if (!el || typeof el.animate !== 'function') { coverShut.value = true; forget(); return }
  const strap = coverRef.value.strapEl
  if (strap) strap.style.opacity = '0'
  setTimeout(() => { coverShut.value = true }, 250)
  await el.animate([{ transform: 'rotateY(-180deg)' }, { transform: 'rotateY(0deg)' }], { duration: 950, easing: COVER_EASE }).finished.catch(() => {})
  forget()
  await nextTick()
  const s = coverRef.value && coverRef.value.strapEl; const k = coverRef.value && coverRef.value.claspEl
  if (s) { s.style.opacity = ''; s.animate([{ transform: 'translateX(90px)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 380, easing: 'cubic-bezier(.2,.8,.3,1.2)' }) }
  if (k) k.animate([{ transform: 'rotate(-28deg)' }, { transform: 'rotate(-28deg)', offset: 0.6 }, { transform: 'scale(1.15)', offset: 0.8 }, { transform: 'none' }], { duration: 640 })
}

async function opened (token) {
  openClasp(token)
  lockErr.value = ''; lockEntry.value = ''; resetToken.value = ''
  // The clasp's state as it is now (on, with or without a passkey), not as it was before.
  try { status.value = (await getJournalStatus(store.session_id)) || status.value } catch (e) { /* keep the last */ }
  const b = await fetchBook().catch(() => null)
  if (!b || b.locked) { shutClasp(); lockErr.value = 'The journal could not open just now.'; lockStep.value = 'cover'; return }
  book.value = keepPractice(b)
  cur.value = startDay(b)
  await swingOpen()
  loadOlder()
  loadThreads()
  loadKept()
}

function lockError (r) {
  if (r && r.reason === 'too_many') return 'Too many tries. Wait a little, or use Forgot your PIN?'
  const left = r && Number(r.attempts_left)
  if (left && left <= 2) return `That’s not it. ${left === 1 ? 'One try' : 'Two tries'} left.`
  return 'That’s not it. Try again.'
}

async function unlockWith (pin) {
  lockBusy.value = true
  try {
    const r = await journalUnlock(store.session_id, pin)
    if (r && r.ok && r.unlock_token) { lockBusy.value = false; await opened(r.unlock_token); return }
    lockEntry.value = ''; lockErr.value = lockError(r); shake.value++
  } catch (e) {
    lockEntry.value = ''; lockErr.value = 'The journal could not open just now.'; shake.value++
  } finally { lockBusy.value = false }
}

async function onBio () {
  lockBusy.value = true; lockErr.value = ''
  try {
    const o = await journalPasskeyOptions(store.session_id)
    if (!o || !o.ok) throw new Error((o && o.reason) || 'no_passkey')
    const credential = await getPasskey(o.options)
    const v = await journalPasskeyVerify(store.session_id, credential)
    if (!v || !v.ok || !v.unlock_token) throw new Error('passkey_failed')
    lockBusy.value = false
    await opened(v.unlock_token)
  } catch (e) {
    lockBusy.value = false
    lockErr.value = `${bio.value || 'The passkey'} didn’t open it. Use your PIN.`
    lockStep.value = 'pin'
  }
}

function onLockStep (s) { lockStep.value = s; lockEntry.value = ''; lockErr.value = '' }

async function onSendCode () {
  lockBusy.value = true; lockErr.value = ''
  try {
    const r = await journalForgot(store.session_id)
    if (!r || !r.ok) {
      lockErr.value = r && r.reason === 'too_many' ? 'Too many codes this hour. Try again later.' : r && r.reason === 'no_address' ? 'There is no email address on your account to send it to.' : 'The code could not be sent just now.'
      return
    }
    sentTo.value = r.sent_to || ''
    lockStep.value = 'code'; lockEntry.value = ''
  } catch (e) { lockErr.value = 'The code could not be sent just now.' } finally { lockBusy.value = false }
}

async function onLockDigit (d) {
  if (lockBusy.value) return
  if (d === 'del') { lockEntry.value = lockEntry.value.slice(0, -1); return }
  const max = lockStep.value === 'code' ? 6 : 4
  if (lockEntry.value.length >= max) return
  lockEntry.value += d
  lockErr.value = ''
  if (lockEntry.value.length < max) return
  if (lockStep.value === 'pin') return unlockWith(lockEntry.value)
  lockBusy.value = true
  try {
    if (lockStep.value === 'code') {
      const r = await journalForgotVerify(store.session_id, lockEntry.value)
      lockEntry.value = ''
      if (r && r.ok && r.reset_token) { resetToken.value = r.reset_token; lockStep.value = 'newpin'; return }
      lockErr.value = r && r.reason === 'expired' ? 'That code has expired. Go back and send a new one.' : 'That code didn’t match. Check the email and try again.'
      shake.value++
      return
    }
    if (lockStep.value === 'newpin') {
      const r = await journalSetPin(store.session_id, lockEntry.value, { reset_token: resetToken.value })
      lockEntry.value = ''
      if (r && r.ok && r.unlock_token) { lockBusy.value = false; await opened(r.unlock_token); return }
      lockStep.value = 'forgot'
      lockErr.value = 'That didn’t work. Send a new code and try again.'
    }
  } catch (e) {
    lockEntry.value = ''; lockErr.value = 'The journal could not open just now.'
  } finally { lockBusy.value = false }
}

// ── the clasp offer and setup ──────────────────────────────────────────────
// The PIN first (it is the clasp; the server wants it before a passkey), then
// Face ID / Touch ID on top when this device has it. Cancelling the passkey
// leaves a PIN-only clasp.
function claspAdd (fromMenu = false) {
  slipFromMenu.value = fromMenu
  setupPin.value = ''; setupFirst.value = ''; setupErr.value = ''
  slipStep.value = 'pin1'
}
function claspFromMenu () { slipFromMenu.value = true; slipStep.value = 'offer' }
function claspNotNow () { slipStep.value = '' }
// The slip is offered once per person: the server hears when it shows.
watch(showSlip, (on) => {
  if (!on || slipStep.value !== 'offer' || slipFromMenu.value || book.value.clasp.offered) return
  book.value = { ...book.value, clasp: { ...book.value.clasp, offered: true } }
  Promise.resolve(journalOnce(store.session_id, 'clasp_offer')).catch(() => {})
})
async function onSetupDigit (d) {
  if (setupBusy.value) return
  if (d === 'del') { setupPin.value = setupPin.value.slice(0, -1); return }
  if (setupPin.value.length >= 4) return
  setupPin.value += d
  if (setupPin.value.length < 4) return
  if (slipStep.value === 'pin1') { setupFirst.value = setupPin.value; setupPin.value = ''; setupErr.value = ''; slipStep.value = 'pin2'; return }
  if (setupPin.value !== setupFirst.value) { setupPin.value = ''; setupFirst.value = ''; setupErr.value = 'Those didn’t match. Start again.'; slipStep.value = 'pin1'; return }
  setupBusy.value = true
  try {
    const r = await journalSetPin(store.session_id, setupPin.value)
    if (!r || !r.ok) { setupErr.value = 'The clasp could not be set just now.'; setupPin.value = ''; slipStep.value = 'pin1'; return }
    if (r.unlock_token) openClasp(r.unlock_token)
    let passkey = false
    if (devicePasskey.value) {
      try {
        const o = await journalPasskeyRegisterOptions(store.session_id)
        if (o && o.ok) {
          const reg = await journalPasskeyRegister(store.session_id, await createPasskey(o.options))
          passkey = !!(reg && reg.ok)
        }
      } catch (e) { passkey = false /* a PIN alone is a clasp too */ }
    }
    book.value = { ...book.value, clasp: { on: true, passkey, offered: true } }
    slipStep.value = 'done'
  } catch (e) { setupErr.value = 'The clasp could not be set just now.'; setupPin.value = ''; slipStep.value = 'pin1' } finally { setupBusy.value = false }
}
async function claspRemove () {
  try {
    const r = await journalLockOff(store.session_id)
    if (staleLock(r)) return
    if (!r || r.ok === false) throw new Error('no')
    book.value = { ...book.value, clasp: { on: false, passkey: false, offered: true } }
    shutClasp()
    say('The clasp is off. The journal opens without it.')
  } catch (e) { say('The clasp could not be taken off just now.') }
}

// ── the page's things ──────────────────────────────────────────────────────
function openReading (m) {
  const id = m.reading_id
  if (!id) return
  store.latest_reading_session_is_paid = m.is_paid !== false
  store.latest_reading_session = id
  // Closing it comes back to THIS page (ReaderPage returnToJournal).
  store.reading_return_to_journal = cur.value || ''
  emit('close')
  const owner = m.reader_sub || (m.mystic && m.mystic.sub) || ''
  router.push({ path: '/', query: owner ? { reader: owner, reading: id } : { reading: id } })
}
// A reading drawn from a lesson: its line opens the lesson (lesson-redo K3; ?lesson= opens it over Lessons).
function openLesson (m) {
  const id = m.from_lesson && m.from_lesson.id
  if (!id) return
  emit('close')
  router.push({ path: '/', query: { lesson: id } })
}
function openGift (m) {
  if (!m.reading_id) return
  emit('close')
  router.push({ path: '/', query: { reader: m.reader_sub || (m.mystic && m.mystic.sub) || store.current_reader_subdomain || '', reading: m.reading_id, inv: m.invite } })
}
function visitMystic (sub) {
  sheet.value = null
  if (!sub) return
  emit('close')
  // The room's ✕ comes back here (mystic-page 2026-10-29).
  noteRoomOrigin(sub, { kind: 'journal' })
  router.push({ path: '/', query: { reader: sub } })
}

async function onMoment ({ type, m, key }) {
  if (type === 'open-reading') return openReading(m)
  if (type === 'open-lesson') return openLesson(m)
  if (type === 'open-gift' || type === 'open-sent') return openGift(m)
  if (type === 'open-msg') { sheet.value = m; return }
  if (type === 'open-letter') { if (m.url) router.push(m.url); else openReading(m); return }
  const before = book.value
  if (type === 'hide') {
    book.value = withMoment(book.value, key, m.id, { hidden: !m.hidden })
    try { const r = await journalItem(store.session_id, key, m.id, m.hidden ? 'unhide' : 'hide'); if (staleLock(r)) return; if (!r || r.ok === false) throw new Error('no') } catch (e) { book.value = before; say('That line couldn’t be hidden just now.') }
    return
  }
  if (type === 'delete') {
    book.value = withoutMoment(book.value, key, m.id)
    const d = dayOf(book.value, key)
    if (d && !d.moments.length && !d.entry) { book.value = withoutDay(book.value, key); cur.value = nb.value.prev || nb.value.next || newest.value }
    try { const r = await journalItem(store.session_id, key, m.id, 'delete'); if (staleLock(r)) return; if (!r || r.ok === false) throw new Error('no') } catch (e) { book.value = before; cur.value = key; say('That line couldn’t be deleted just now.') }
    return
  }
  if (type === 'take-back') {
    revoking.value = m.id
    try {
      await careRevoke(store.session_id, m.reading_id)
      book.value = withMoment(book.value, key, m.id, { revoked_at: new Date().toISOString(), revocable: false })
    } catch (e) {
      say(`${(m.to && m.to.name) || 'They'} opened it just now, so it stays.`)
      load({ keepDay: true })
    } finally { revoking.value = '' }
  }
}

async function onDayHide (key) {
  const before = book.value
  const day = dayOf(book.value, key)
  if (!day) return
  const hide = !day.hidden
  const { prev, next } = neighbours(keys.value, key)
  book.value = { ...book.value, days: book.value.days.map((d) => (d.key === key ? { ...d, hidden: hide } : d)) }
  if (hide && !showHiddenPages.value) {
    cur.value = prev || next || newest.value
    say(`${shortDate(key)} is hidden. Find it under Hidden pages in a page’s ⋯ menu.`)
  }
  try { const r = await journalPage(store.session_id, key, hide ? 'hide' : 'unhide'); if (staleLock(r)) return; if (!r || r.ok === false) throw new Error('no') } catch (e) { book.value = before; cur.value = key; say('That page couldn’t be hidden just now.') }
}
async function onDayDelete (key) {
  const before = book.value
  const { prev, next } = neighbours(keys.value, key)
  book.value = withoutDay(book.value, key)
  cur.value = prev || next || ''
  try { const r = await journalPage(store.session_id, key, 'delete'); if (staleLock(r)) return; if (!r || r.ok === false) throw new Error('no') } catch (e) { book.value = before; cur.value = key; say('That page couldn’t be deleted just now.') }
}

function onEditSaved ({ key, text }) { book.value = withEntry(book.value, key, { text, edited: true, unseen: false }) }
function onJournalUpdated (e) {
  const { date, journal } = (e && e.detail) || {}
  if (date && journal && (journal.summary || journal.text)) book.value = withEntry(book.value, date, { text: journal.summary || journal.text })
}

// ── the practice: threads, the chapter, and the seeker's own lines ──────────
async function openThread (id, hops = 0) {
  if (id === null || id === undefined || id === '') return
  litThread.value = id
  menu.value = ''; calOpen.value = false; editKey.value = ''
  if (!desktop.value) searchOpen.value = false
  view.value = { kind: 'thread', id }
  thread.value = null; viewLoading.value = true
  scrollTop(0)
  try {
    const r = await getJournalThread(store.session_id, id)
    if (staleLock(r)) return
    // Merged away: turn to the thread it went into.
    if (r && r.ok === false && r.merged_into !== undefined && r.merged_into !== null && hops < 3) { viewLoading.value = false; return openThread(r.merged_into, hops + 1) }
    const t = normalizeThread(r)
    if (!t) throw new Error('no_thread')
    if (view.value && view.value.id === id) thread.value = t
  } catch (e) { if (view.value && view.value.id === id) view.value = null; say('That thread could not open just now.') } finally { viewLoading.value = false }
}
async function openChapter (month) {
  if (!month) return
  menu.value = ''; calOpen.value = false; editKey.value = ''
  if (!desktop.value) searchOpen.value = false
  view.value = { kind: 'chapter', month }
  chapter.value = null; viewLoading.value = true
  scrollTop(0)
  try {
    const r = await getJournalChapter(store.session_id, month)
    if (staleLock(r)) return
    const c = normalizeChapter(r)
    if (!c) throw new Error('no_chapter')
    if (view.value && view.value.month === month) chapter.value = c
  } catch (e) { if (view.value && view.value.month === month) view.value = null; say('That chapter could not open just now.') } finally { viewLoading.value = false }
}
function backToDay () { view.value = null; nextTick(() => scrollTop(0)) }

// ── Kept, and you & them ────────────────────────────────────────────────────
async function loadKept () {
  if (book.value.locked) return
  keptLoading.value = true
  try {
    const r = await getJournalKept(store.session_id)
    if (staleLock(r)) return
    if (r && r.ok !== false) { kept.value = normalizeKept(r); stars.value = normalizeOurStars(r.you_and) }
  } catch (e) { /* the row waits; nothing is claimed */ } finally { keptLoading.value = false }
}
function openKept () {
  menu.value = ''; calOpen.value = false; editKey.value = ''
  if (!desktop.value) searchOpen.value = false
  view.value = { kind: 'kept' }
  scrollTop(0)
  loadKept()
}
function openStars (id) {
  menu.value = ''; calOpen.value = false; editKey.value = ''
  if (!desktop.value) searchOpen.value = false
  view.value = { kind: 'stars', id }
  scrollTop(0)
}
/** A kept thing opens the day it was kept. */
function openKeptDay (key) {
  if (keys.value.includes(key)) { turnTo(key); return }
  say('That day’s page isn’t in your journal any more.')
}
/** "Remove from Kept": off the shelf only; the drop, lesson or card stays where it lives. */
async function onUnkeep (it) {
  const before = kept.value
  const items = before.items.filter((x) => x.id !== it.id)
  const n = Math.max(0, before.count - 1)
  kept.value = { ...before, items, count: n, thumbs: thumbsOf(items), line: '' }
  try {
    const r = await journalUnkeep(store.session_id, it.id)
    if (!r || r.kept !== false) throw new Error('no')
  } catch (e) { kept.value = before; say('That couldn’t be removed just now.') }
}
function searchFromView () { view.value = null; onSearch() }

const ok = (r) => !!r && r.ok !== false
/** The practice's own state survives a reload of the days. */
function keepPractice (b) { return b.locked ? b : { ...b, threads: book.value.threads || [], chapterStates: book.value.chapterStates || {} } }
async function loadThreads () {
  try { const r = await getJournalThreads(store.session_id); if (ok(r) && Array.isArray(r.threads)) book.value = { ...book.value, threads: normalizeThreadList(r) } } catch (e) { /* "Or add" waits */ }
}
// The left page's month: its chapter state from journal/calendar/, once per month.
const asking = new Set()
async function onMonth (mk) {
  if (!mk || asking.has(mk) || book.value.locked || mk in (book.value.chapterStates || {})) return
  asking.add(mk)
  try {
    const r = await getJournalCalendar(store.session_id, mk)
    if (staleLock(r)) return
    if (ok(r) && 'chapter' in r) book.value = withChapterState(book.value, mk, r.chapter || null)
  } catch (e) { /* the line under the list waits */ } finally { asking.delete(mk) }
}
/** The server's whole day after a save (journal/line/ → day) is what the page shows. */
function tookDay (r) { if (r && r.day && r.day.date) book.value = keepPractice(withDay(book.value, r.day)) }
const stepsWith = (t, readingId, piece, text) => (t ? { ...t, steps: t.steps.map((s) => (s.readingId === readingId ? { ...s, [piece]: text, [piece + 'Edited']: true } : s)) } : t)
/** One of their own lines (gut | landed), from the day page or a thread's step. */
async function onOwn ({ key, piece, text, readingId }) {
  const before = book.value; const beforeThread = thread.value
  book.value = withOwn(book.value, key, piece, text, readingId)
  thread.value = stepsWith(thread.value, readingId, piece, text)
  try {
    const r = await journalLine(store.session_id, key, piece, text, { reading_id: readingId })
    if (staleLock(r)) return
    if (!ok(r)) throw new Error('no')
    tookDay(r)
  } catch (e) { book.value = before; thread.value = beforeThread; say('That line didn’t save. Try again.') }
}
async function onTags ({ key, renames, removed, added, threads }) {
  const before = book.value
  let b = book.value
  for (const r of renames) b = withThreadName(b, r.id, r.name)
  book.value = withDayThreads(b, key, threads)
  // Each answer carries the day's tags as they now are: the last one is the page's.
  let last = null
  try {
    for (const r of renames) { const x = await journalThreadRename(store.session_id, r.id, r.name); if (staleLock(x)) return; if (!ok(x)) throw new Error('no') }
    for (const id of removed) { const x = await journalThreadUntag(store.session_id, key, id); if (staleLock(x)) return; if (!ok(x)) throw new Error('no'); last = x }
    for (const a of added) {
      const x = await journalThreadTag(store.session_id, key, a.id !== '' && a.id !== null && a.id !== undefined ? { thread_id: a.id } : { name: a.name })
      if (staleLock(x)) return
      if (!ok(x)) throw new Error('no')
      last = x
      const t = x.thread
      if (t && t.id !== undefined && !(book.value.threads || []).some((y) => y.id === t.id)) book.value = { ...book.value, threads: [{ id: t.id, name: String(t.name || a.name) }, ...(book.value.threads || [])] }
    }
    if (last && Array.isArray(last.threads)) book.value = withDayThreads(book.value, key, last.threads.map(normalizeThreadTag))
  } catch (e) { book.value = before; say('Those threads didn’t save. Try again.'); load({ keepDay: true }) }
}
async function onRename ({ id, name }) {
  const before = book.value; const beforeThread = thread.value
  book.value = withThreadName(book.value, id, name)
  if (thread.value && thread.value.id === id) thread.value = { ...thread.value, name, nameIsUser: true }
  try {
    const r = await journalThreadRename(store.session_id, id, name)
    if (staleLock(r)) return
    if (!ok(r)) throw new Error(r && r.reason)
  } catch (e) {
    book.value = before; thread.value = beforeThread
    say(e && e.message === 'bad_name' ? 'A thread’s name is 1 to 60 characters.' : 'That name didn’t save. Try again.')
  }
}
async function onChapterSave (text) {
  const before = chapter.value
  const month = before && before.month
  if (!month) return
  chapter.value = { ...before, edited: !!text, text }
  try {
    const r = await journalChapterSave(store.session_id, month, text)
    if (staleLock(r)) return
    if (!ok(r)) throw new Error('no')
    const c = normalizeChapter(r)
    if (c) chapter.value = c
  } catch (e) { chapter.value = before; say('Your chapter didn’t save. Try again.') }
}
// The one-time line shows on the first practice page they see, this visit only.
watch(() => !!(curDay.value && (curDay.value.own || (curDay.value.threads || []).length)), (on) => {
  if (on && hint.value) { try { window.localStorage.setItem(HINT_KEY, '1') } catch (e) { /* shown again next time; no harm */ } }
}, { immediate: true })

function onCal () { calOpen.value = !calOpen.value; menu.value = '' }
function onSearch () { searchOpen.value = true; calOpen.value = false; menu.value = ''; nextTick(() => { const i = rootEl.value && rootEl.value.querySelector('#journal-q'); if (i) i.focus() }) }
function gotoFromContents (k) { calOpen.value = false; if (!desktop.value) { searchOpen.value = false; query.value = '' } turnTo(k) }

// Leaving the Journal shuts the clasp; coming back reads the newest book.
function onAltarTab (e) {
  const tab = e && e.detail
  if (tab === 'history') {
    if (cur.value) emit('dayChange', cur.value)
    nextTick(onBecameVisible)
    if (!book.value.locked) load({ keepDay: true })
    return
  }
  editKey.value = ''
  if (book.value.clasp.on) closeClasp({ animate: false })
}

// The server's token lives 5 minutes from the last journal call. Reading a
// long page makes no calls, so keep it fresh while the Journal is in view.
let refreshTimer = null
function startRefresh () {
  clearInterval(refreshTimer)
  refreshTimer = setInterval(async () => {
    if (!clasp.open || !book.value.clasp.on || !visible() || document.visibilityState === 'hidden') return
    try { staleLock(await journalLockRefresh(store.session_id)) } catch (e) { /* the next call says */ }
  }, 4 * 60 * 1000)
}
let io = null
let stopAway = null
let ro = null
let mq = null
const onMq = () => { reduced.value = !!(mq && mq.matches) }
onMounted(async () => {
  try { mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)'); onMq(); mq && mq.addEventListener && mq.addEventListener('change', onMq) } catch (e) { /* */ }
  if (typeof ResizeObserver !== 'undefined' && rootEl.value) {
    ro = new ResizeObserver((es) => { const w = es[0].contentRect.width; desktop.value = w >= 900 })
    ro.observe(rootEl.value)
  }
  // A tab shown by v-show fires no event of ours on a cold load: watch for it.
  if (typeof IntersectionObserver !== 'undefined' && rootEl.value) {
    io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) onBecameVisible() })
    io.observe(rootEl.value)
  }
  window.addEventListener('keydown', onKey)
  window.addEventListener('altar-tab-changed', onAltarTab)
  window.addEventListener('journal-updated', onJournalUpdated)
  document.addEventListener('click', onDocClick)
  stopAway = watchAway(() => { if (book.value.clasp.on) closeClasp({ animate: false }) })
  passkeyAvailable().then((v) => { devicePasskey.value = v })
  startRefresh()
  await load()
})
onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('altar-tab-changed', onAltarTab)
  window.removeEventListener('journal-updated', onJournalUpdated)
  document.removeEventListener('click', onDocClick)
  stopAway && stopAway()
  ro && ro.disconnect()
  io && io.disconnect()
  mq && mq.removeEventListener && mq.removeEventListener('change', onMq)
  clearTimeout(playTimer)
  clearInterval(refreshTimer)
  olderRun++
  if (book.value.clasp.on) shutClasp()
})

defineExpose({ turnTo, closeClasp, book, cur })
const sheetText = computed(() => (sheet.value ? plainEntry(sheet.value.text || '') : ''))
</script>

<template>
  <div ref="rootEl" class="jb" :class="{ desk: desktop, rm: reduced }" data-testid="journal-book">
    <div v-if="loading" class="jb-loading" aria-live="polite">Opening your journal…</div>

    <div v-else class="book" :class="{ shut: coverShut && desktop, closed: locked }">
      <!-- 1440: the left page is the contents. -->
      <div v-if="desktop" class="lp">
        <div v-if="!locked" class="page lpage"><div class="lscroll"><div class="linner">
          <JournalContents mode="spread" :book="book" :cur="cur" :hidden-days="hidden" v-model:query="query" :open-thread="litThread" @goto="gotoFromContents"
                           :kept="kept" :stars="stars" :open-view="openView" @kept="openKept" @stars="openStars"
                           @thread="openThread" @chapter="openChapter" @month="onMonth" />
        </div></div></div>
      </div>

      <div class="hold" :class="{ rp: desktop }" @pointerdown="onPointerDown" @pointerup="onPointerUp">
        <div v-if="locked" class="pagesedge"></div>
        <template v-else>
          <JournalPage ref="pageRef" :day="curDay" :book="book" :prev="view ? '' : nb.prev" :next="view ? '' : nb.next" :newest="newest" :desktop="desktop"
                       :played="reduced || !!played[cur]" :writing="writingKey === cur && !!cur"
                       :menu="menu" :revoking="revoking" :cover="!!cover" :clasp-on="book.clasp.on" :hidden-pages="hiddenCount" :showing-hidden-pages="showHiddenPages"
                       :searching="(searchOpen && !desktop) || editing || !!view" :cal-open="calOpen" :others="dayOthers" :hint="hint"
                       @turn="turnTo" @cal="onCal" @search="onSearch" @menu="menu = $event" @edit="editKey = $event" @moment="onMoment"
                       @day-hide="onDayHide" @day-delete="onDayDelete" @clasp-add="claspFromMenu" @clasp-lock="closeClasp()" @clasp-remove="claspRemove"
                       @hidden-pages="showHiddenPages = !showHiddenPages" @thread="openThread" @own="onOwn" @tags="onTags">
            <template #search>
              <JournalEditor v-if="editing" :day-key="cur" :text="editText" :word-count="book.wordCount" @close="editKey = ''" @saved="onEditSaved" @length="book = { ...book, wordCount: $event }" />
              <JournalThread v-else-if="view && view.kind === 'thread'" :thread="thread" :loading="viewLoading" :back-label="backLabel"
                             @back="backToDay" @goto="turnTo" @rename="onRename" @line="onOwn" />
              <JournalChapter v-else-if="view && view.kind === 'chapter'" :chapter="chapter" :loading="viewLoading" :back-label="backLabel" @back="backToDay" @save="onChapterSave" />
              <JournalKept v-else-if="view && view.kind === 'kept'" :kept="kept" :loading="keptLoading" :desktop="desktop" :menu="menu" :cal-open="calOpen" :back-label="backLabel"
                           @menu="menu = $event" @cal="onCal" @search="searchFromView" @back="backToDay" @goto="openKeptDay" @unkeep="onUnkeep" />
              <JournalStars v-else-if="view && view.kind === 'stars'" :stars="openStarsItem" :desktop="desktop" :cal-open="calOpen" :back-label="backLabel" @cal="onCal" @back="backToDay" />
              <JournalContents v-else mode="search" :book="book" :cur="cur" :hidden-days="hidden" v-model:query="query" @goto="gotoFromContents" @cancel="searchOpen = false; query = ''"
                               @thread="openThread" @chapter="openChapter" />
            </template>
            <template #slip>
              <JournalClaspSlip v-if="showSlip" :step="slipStep" :pin="setupPin" :err="setupErr" :bio="bio" :busy="setupBusy" :animate="!reduced && slipStep === 'offer' && !played[cur]"
                                @add="claspAdd(slipFromMenu)" @not-now="claspNotNow" @digit="onSetupDigit" @lock-now="closeClasp()" />
            </template>
            <template #sheet>
              <div v-if="calOpen && !desktop" class="calsheet" @click.stop>
                <JournalContents mode="cal" :book="book" :cur="cur" :hidden-days="hidden" :open-thread="litThread" @goto="gotoFromContents" @thread="openThread" @chapter="openChapter" @month="onMonth"
                                 :kept="kept" :stars="stars" :open-view="openView" @kept="openKept" @stars="openStars" />
              </div>
            </template>
          </JournalPage>

          <div v-if="leaf" ref="leafEl" class="leaf">
            <JournalPage ref="leafPageRef" leaf :day="leafDay" :book="book" :prev="''" :next="''" :newest="''" :desktop="desktop" played />
            <div ref="shadeEl" class="shade"></div>
          </div>
        </template>

        <JournalCover v-if="cover" ref="coverRef" :mode="cover" :name="book.name" :step="lockStep" :entry="lockEntry" :err="lockErr" :email="sentTo"
                      :bio="book.clasp.passkey ? bio : ''" :busy="lockBusy" :shake="shake" :shine="cover === 'first'" :reduced="reduced"
                      @bio="onBio" @step="onLockStep" @digit="onLockDigit" @send-code="onSendCode" />
      </div>
    </div>

    <!-- A Mystic's message, opened from its photo. -->
    <div v-if="sheet" class="scrim" @click.self="sheet = null">
      <div class="sheet" role="dialog" :aria-label="sheet.title || 'A message'" data-testid="journal-msg-sheet">
        <div class="hd">
          <span class="sq" :class="{ round: !sheet.mystic && sheet.from }"><img v-if="sheet.mystic && sheet.mystic.avatar" :src="sheet.mystic.avatar" alt="" /><img v-else-if="!sheet.mystic && sheet.from && sheet.from.avatar" :src="sheet.from.avatar" alt="" /></span>{{ (sheet.mystic && sheet.mystic.name) || (sheet.from && sheet.from.name) || 'Mystic Aida' }}
        </div>
        <img v-if="sheet.image_url" class="art" :src="sheet.image_url" alt="" />
        <h3>{{ sheet.title }}</h3>
        <p v-if="sheetText">{{ sheetText }}</p>
        <div class="row">
          <button v-if="sheet.mystic && sheet.mystic.sub" type="button" class="gbtn" @click="visitMystic(sheet.mystic.sub)">Visit {{ sheet.mystic.name }}</button>
          <button type="button" class="ghost-d" @click="sheet = null">Close</button>
        </div>
      </div>
    </div>

    <div v-if="toast" class="toast" role="status">{{ toast }}</div>
  </div>
</template>

<style scoped>
.jb { --j-gold: #c9a94e; --j-paper: #f2e9d6; --j-paper2: #e6dac0; --j-ink: #2a1845; --j-ink2: #58466f; --j-ink3: #8a7a9c; --j-hand: #8c6628; --j-rule: rgba(88, 70, 111, .10);
  --j-leather: #3a1d5c; --j-leather2: #22103a;
  --j-display: 'Letter Display', 'Cinzel', Georgia, serif; --j-serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif; --j-ui: Inter, system-ui, -apple-system, 'Segoe UI', sans-serif;
  position: absolute; inset: 0; overflow: hidden; color: #efe4ff; font-family: var(--j-ui); -webkit-font-smoothing: antialiased;
  background: radial-gradient(120% 55% at 50% 0%, #1d0d38 0%, #0f0624 55%, #0b041b 100%); }
.jb::before { content: ''; position: absolute; inset: 0; pointer-events: none; opacity: .7; background:
  radial-gradient(1px 1px at 12% 18%, #fff8 50%, transparent 51%), radial-gradient(1px 1px at 82% 9%, #fff7 50%, transparent 51%),
  radial-gradient(1.4px 1.4px at 64% 92%, #ffe7b588 50%, transparent 51%), radial-gradient(1px 1px at 30% 96%, #fff6 50%, transparent 51%),
  radial-gradient(1.2px 1.2px at 93% 60%, #fff6 50%, transparent 51%), radial-gradient(1px 1px at 4% 70%, #fff5 50%, transparent 51%); }
.jb-loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font: italic 500 18px var(--j-serif); color: rgba(255, 231, 181, .7); }

/* the phone: one page in the book */
.book { position: absolute; inset: 0; }
.hold { position: absolute; left: 10px; right: 12px; top: 10px; bottom: 12px; perspective: 2200px; }
.hold::before, .hold::after { content: ''; position: absolute; border-radius: 3px 10px 10px 3px; background: var(--j-paper2); }
.hold::before { left: 2px; right: -4px; top: 4px; bottom: -4px; box-shadow: 0 8px 24px rgba(0, 0, 0, .55); filter: brightness(.9); }
.hold::after { left: 1px; right: -2px; top: 2px; bottom: -2px; }
.closed .hold::before, .closed .hold::after { display: none; }
.pagesedge { position: absolute; z-index: 11; left: 3px; right: -4px; top: 3px; bottom: -4px; border-radius: 3px 12px 12px 3px; background: repeating-linear-gradient(180deg, #eadfc6 0 2px, #d9cbaa 2px 3px); }
.leaf { position: absolute; inset: 0; z-index: 6; transform-origin: left center; backface-visibility: hidden; -webkit-backface-visibility: hidden; will-change: transform; }
.shade { position: absolute; inset: 0; z-index: 7; pointer-events: none; border-radius: 3px 10px 10px 3px; background: linear-gradient(90deg, rgba(40, 20, 10, 0), rgba(40, 20, 10, .35)); opacity: 0; }

/* 1440: the open book */
.desk .book { left: 50%; top: 14px; bottom: 30px; right: auto; width: min(968px, calc(100% - 48px)); transform: translateX(-50%); transition: transform 1.1s cubic-bezier(.45, .05, .25, 1); }
.desk .book.shut { transform: translateX(-75%); } /* the closed right half sits centred */
.desk .book::before { content: ''; position: absolute; inset: -9px -10px -11px; border-radius: 12px; background: linear-gradient(90deg, var(--j-leather2), var(--j-leather) 48%, #170a29 50%, var(--j-leather) 52%, var(--j-leather2)); box-shadow: 0 18px 50px rgba(0, 0, 0, .6); transition: opacity .5s; }
.desk .book.shut::before, .desk .book.closed::before { opacity: 0; }
.desk .lp, .desk .rp { position: absolute; top: 0; bottom: 0; width: 50%; }
.desk .lp { left: 0; transition: opacity .45s .5s; }
.desk .book.shut .lp { opacity: 0; transition-delay: 0s; }
.desk .hold.rp { left: auto; right: 0; perspective: 2600px; }
.desk .hold.rp::before, .desk .hold.rp::after { display: none; }
.desk .hold.rp :deep(.page) { border-radius: 2px 10px 10px 2px; }
.desk .closed .pagesedge { left: 6px; top: 6px; }
.lpage { position: absolute; inset: 0; border-radius: 10px 2px 2px 10px; overflow: hidden; color: var(--j-ink); background-color: var(--j-paper);
  background-image: radial-gradient(130% 90% at 40% 40%, rgba(255, 252, 240, .55), rgba(214, 196, 160, 0) 60%, rgba(180, 150, 110, .22) 100%); }
.lpage::before { content: ''; position: absolute; right: 0; top: 0; bottom: 0; width: 26px; z-index: 4; pointer-events: none; background: linear-gradient(270deg, rgba(70, 40, 30, .3), rgba(70, 40, 30, .06) 45%, transparent); }
.lscroll { position: absolute; inset: 0; overflow-y: auto; scrollbar-width: none; }
.lscroll::-webkit-scrollbar { display: none; }
.linner { padding: 0 34px 40px 30px; min-height: 100%; box-sizing: border-box; background-image: repeating-linear-gradient(to bottom, transparent 0 31px, var(--j-rule) 31px 32px); background-position: 0 12px; }

/* the contents sheet on a phone (☰ Contents) */
.calsheet { position: absolute; left: 10px; right: 10px; top: 96px; max-height: calc(100% - 110px); overflow-y: auto; scrollbar-width: none; z-index: 8; border-radius: 12px; padding: 12px 14px 14px; background: #fbf6ea; box-sizing: border-box;
  box-shadow: 0 14px 34px rgba(42, 24, 69, .3), 0 0 0 1px rgba(42, 24, 69, .1); transform-origin: top center; animation: unfold .28s cubic-bezier(.2, .8, .3, 1); }
@keyframes unfold { from { opacity: 0; transform: scaleY(.94) translateY(-6px); } }
.rm .calsheet { animation: none; }

/* a Mystic's message */
.scrim { position: absolute; inset: 0; z-index: 30; background: rgba(8, 3, 18, .6); display: flex; align-items: flex-end; justify-content: center; animation: fade .2s; }
.desk .scrim { align-items: center; }
@keyframes fade { from { opacity: 0; } }
.sheet { width: 100%; max-width: 440px; max-height: 92%; overflow-y: auto; background: #160b2b; border-radius: 18px 18px 0 0; padding: 16px 16px 22px; color: #efe4ff; box-shadow: 0 -10px 40px rgba(0, 0, 0, .5); animation: up .28s cubic-bezier(.2, .8, .3, 1); box-sizing: border-box; }
.desk .sheet { border-radius: 18px; }
@keyframes up { from { transform: translateY(30px); opacity: 0; } }
.rm .sheet, .rm .scrim { animation: none; }
.sheet .hd { display: flex; align-items: center; gap: 10px; font: 600 17px var(--j-serif); color: #fff4dc; }
.sheet .sq { width: 28px; height: 28px; border-radius: 6px; overflow: hidden; flex: none; background: #3a1d5c; box-shadow: 0 0 0 1px rgba(201, 169, 78, .5); }
.sheet .sq img { width: 100%; height: 100%; object-fit: cover; }
.sheet .sq.round { border-radius: 50%; }
.sheet .art { width: 100%; height: auto; aspect-ratio: 16 / 10.5; object-fit: cover; border-radius: 12px; margin: 12px 0 10px; display: block; }
.sheet h3 { margin: 10px 0 0; font: italic 600 24px var(--j-serif); color: #ffe7b5; }
.sheet p { margin: 6px 0 0; font: 500 17px/1.45 var(--j-serif); color: #e6dafa; }
.sheet .row { display: flex; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
.gbtn { display: inline-flex; align-items: center; gap: 8px; padding: 11px 20px; min-height: 44px; border-radius: 999px; background: linear-gradient(180deg, #f3d58f, #d9b25e); color: #2a1845; font: 700 16px var(--j-serif); border: 0; cursor: pointer; }
.ghost-d { padding: 11px 18px; min-height: 44px; border-radius: 999px; border: 1px solid rgba(255, 223, 163, .45); color: #ffdfa3; font: 600 16px var(--j-serif); background: none; cursor: pointer; }
.toast { position: absolute; left: 50%; bottom: 24px; z-index: 35; transform: translateX(-50%); background: #fff4dc; color: #2a1845; font: 600 13px/1.35 var(--j-ui); padding: 10px 14px; border-radius: 12px; box-shadow: 0 8px 24px rgba(0, 0, 0, .45); width: max-content; max-width: min(330px, calc(100% - 32px)); text-align: center; animation: fade .2s; }
</style>
